import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  OnInit,
  computed,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { DialogData } from '../../../../shared/models/dialog-data.model';
import { MatButtonModule } from '@angular/material/button';
import { InputField } from '../../../../shared/components/input-field/input-field';
import {
  ALIAS_CONTROLS,
  SITE_CONTROLS,
  SUBDOMAIN_CONTROLS,
  TYPES,
  WP_FORM_ERROR_MESSAGES,
} from '../../../dashboard/dialogs/add-website-dialog/add-website-dialog.const';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  CreateWebsiteAliasDTO,
  CreateWebsiteDTO,
  CreateWebsiteSubdomainDTO,
} from '../../../../shared/models/website-create.models';
import { TemporaryWPCredentials } from '../../../../shared/models/wordpress.models';
import {
  Control,
  FormType,
  WebsiteFormValue,
} from '../../../dashboard/dialogs/add-website-dialog/add-website-dialog.models';
import {
  mapToAliasCreateRequest,
  mapToSubdomainCreateRequest,
  mapToWebsiteCreateRequest,
  mapToWPCredentialsRequest,
} from '../../../dashboard/dialogs/add-website-dialog/website-request.mapper';
import { AuthService } from '../../../../core/services/auth.service';
import { WebsiteExtendedWithOriginVhost } from '../../../../shared/models/group-overview.model';
import { map, tap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { Server } from '../../../../shared/models/website-overview.model';

@Component({
  selector: 'app-add-website-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    InputField,
    FontAwesomeModule,
    MatCheckboxModule,
    AsyncPipe,
  ],
  templateUrl: './add-website-dialog.component.html',
  styleUrl: './add-website-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddWebsiteDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<AddWebsiteDialogComponent>);

  readonly data = inject<
    DialogData & {
      websites: WebsiteExtendedWithOriginVhost[];
      owner: string;
      server: Server;
      plan: string;
      groupId: number;
    }
  >(MAT_DIALOG_DATA);

  private fb = inject(FormBuilder);

  private authService = inject(AuthService);

  readonly types = TYPES.filter((t) => t.label !== 'Main');

  siteControls: Control[] = SITE_CONTROLS.filter(
    (c) => c.controlName !== 'originServer' && c.controlName !== 'groupId',
  );

  aliasControls: Control[] = ALIAS_CONTROLS;

  subdomainControls: Control[] = SUBDOMAIN_CONTROLS;

  readonly wpErrors = WP_FORM_ERROR_MESSAGES;

  controlsArray = this.siteControls;

  currentUser = this.authService.getUser()!;

  form = this.fb.nonNullable.group({
    type: [FormType.SITE.toString(), Validators.required], // Utility control
    originServer: [''],
    groupId: [''],
    domain: ['', [this.domainValidator]],
    userId: [''],
    enableEmail: [true], // Utility control
    installWordpress: [false], // Utility control

    websiteId: [''], // Just so I can use the shared logic
    subdomainMain: [''], // Just so I can use the shared logic
    plan: [''], // Just so I can use the shared logic
  });

  wordpressForm = this.fb.nonNullable.group({
    domain: [''],
    user: ['', Validators.required],
    password: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    enableRedisCache: [false],
    enableW3TotalCache: [false], // Utility control
    enableWooCommerce: [false], // Utility control
    plugins: [],
  });

  step = 1;

  title = 'Add website in group';

  primaryButtonText = 'Add Website';

  websiteSearchText = signal<string>('');

  websites = computed(() =>
    this.data.websites
      .filter((w) => w.domain.includes(this.websiteSearchText()))
      .map((w) => ({ label: w.domain, value: String(w.id) })),
  );

  websiteSuffixText$ = this.form.controls.websiteId.valueChanges.pipe(
    map((id) => {
      const domain = this.data.websites.find((w) => w.id === +id)?.domain;

      if (this.form.controls.type.value === FormType.SUBDOMAIN.toString() && domain)
        return `.${domain}`;
      return '';
    }),
    tap((domain) => this.form.controls.subdomainMain.setValue(domain)),
  );

  ngOnInit(): void {
    this.form.controls.groupId.setValue(this.data.groupId.toString());
    this.form.controls.originServer.setValue(this.data.server.id);
    this.form.controls.userId.setValue(this.currentUser.id);

    this.listenForChanges();
    this.updateValidators(FormType.SITE.toString());
  }

  listenForChanges(): void {
    this.form.controls.installWordpress.valueChanges.subscribe((enabled) => {
      this.primaryButtonText = enabled ? 'Continue' : 'Add Website';
    });

    this.form.controls.type.valueChanges.subscribe((type) => {
      this.form.reset({ type }, { emitEvent: false });
      this.updateValidators(type);
      this.updateForm(type);
    });

    this.form.controls.originServer.valueChanges.subscribe((server) => {
      if (server) this.form.controls.groupId.enable();
      else this.form.controls.groupId.disable();
      this.form.controls.groupId.updateValueAndValidity();
    });

    this.form.controls.domain.valueChanges.subscribe((domain) =>
      this.wordpressForm.controls.domain.setValue(domain),
    );
  }

  updateValidators(type: string): void {
    const siteControls = SITE_CONTROLS.filter((c) => c.type !== 'checkbox').map(
      (c) => c.controlName,
    );
    const aliasControls = ALIAS_CONTROLS.filter((c) => c.type !== 'checkbox').map(
      (c) => c.controlName,
    );
    const subdomainControls = SUBDOMAIN_CONTROLS.filter((c) => c.type !== 'checkbox').map(
      (c) => c.controlName,
    );

    Object.values(this.form.controls).forEach((control) => {
      control.clearValidators();
      control.updateValueAndValidity({ emitEvent: false });
    });

    switch (type) {
      case FormType.ALIAS.toString():
        aliasControls.forEach((control) => {
          this.form.get(control)?.setValidators(Validators.required);
          this.form.controls.domain.addValidators(this.domainValidator);
          this.form.get(control)?.updateValueAndValidity();
        });
        break;
      case FormType.SUBDOMAIN.toString():
        subdomainControls.forEach((control) => {
          this.form.get(control)?.setValidators(Validators.required);
          this.form.get(control)?.updateValueAndValidity();
        });
        break;
      default:
        siteControls.forEach((control) => {
          this.form.get(control)?.setValidators(Validators.required);
          this.form.controls.domain.addValidators(this.domainValidator);
          this.form.get(control)?.updateValueAndValidity();
        });
        break;
    }
  }

  updateForm(type: string): void {
    switch (type) {
      case FormType.ALIAS.toString():
        this.controlsArray = this.aliasControls;
        break;
      case FormType.SUBDOMAIN.toString():
        this.controlsArray = this.subdomainControls;
        break;
      default:
        this.controlsArray = this.siteControls;
        break;
    }
  }

  submit(): void {
    const installWp = this.form.controls.installWordpress.value;

    if (this.step === 1 && installWp) {
      this.step = 2;

      this.title = 'Install WordPress';
      this.primaryButtonText = 'Add Website';

      return;
    }

    const result: {
      type: FormType;
      website: CreateWebsiteDTO | CreateWebsiteAliasDTO | CreateWebsiteSubdomainDTO;
      wordpress?: TemporaryWPCredentials;
    } = {
      type: FormType[this.form.controls.type.getRawValue() as keyof typeof FormType],
      website: this.getWebsiteRequestData(this.form.getRawValue()),
    };
    if (this.step === 2)
      result.wordpress = mapToWPCredentialsRequest(this.wordpressForm.getRawValue());

    this.dialogRef.close(result);
  }

  close(): void {
    this.dialogRef.close();
  }

  private getWebsiteRequestData(
    formValue: WebsiteFormValue,
  ): CreateWebsiteDTO | CreateWebsiteAliasDTO | CreateWebsiteSubdomainDTO {
    switch (formValue.type) {
      case FormType.ALIAS.toString():
        return mapToAliasCreateRequest(formValue);
      case FormType.SUBDOMAIN.toString():
        return mapToSubdomainCreateRequest(formValue);
      default:
        return mapToWebsiteCreateRequest(formValue);
    }
  }

  private domainValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const isValid = !!value.match(/\w{3,}\.[a-z]{2,}/);
    return isValid ? null : { invalidDomain: true };
  }
}
